import React, { useEffect, useState } from "react";
import http from "../../../../services/http";
import { PrivateFileAnchor } from "../../../../utils/privateFile";

/**
 * 28/10/2026 (Quân, bước 4): trạm chỉ định tài khoản nhận tiền (tick người đại diện / văn bản chỉ định).
 * Admin đọc đủ vết rồi duyệt hoặc từ chối. Duyệt xong cổng rút tiền mới nhận tài khoản đó.
 */
type ChiDinh = {
	id: string;
	holderName: string;
	mode: "representative" | "letter";
	files: string[];
	signerName: string;
	statement: string;
	statementVersion: string;
	status: "pending" | "approved" | "rejected" | "replaced";
	submittedByEmail: string | null;
	ip: string | null;
	userAgent: string | null;
	submittedAt: string;
	reviewNote: string | null;
	reviewedAt: string | null;
};
const MAU: Record<string, string> = { approved: "#067647", rejected: "#B42318", pending: "#B54708", replaced: "#667085" };

export default function ChiDinhNguoiNhanTram(props: { partnerId: string; onChanged?: () => void }) {
	const [ds, setDs] = useState<ChiDinh[]>([]);
	const [busy, setBusy] = useState("");
	const [lyDo, setLyDo] = useState<Record<string, string>>({});
	const [loi, setLoi] = useState("");
	const tai = () =>
		http
			.get(`/admin/partners/${props.partnerId}/payout-designations`)
			.then(({ data }) => setDs(data?.data || []))
			.catch(() => setDs([]));
	useEffect(() => {
		tai();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.partnerId]);
	const duyet = async (id: string, status: "approved" | "rejected") => {
		if (status === "rejected" && !String(lyDo[id] || "").trim()) {
			setLoi("Please write a reason so the station knows what to fix.");
			return;
		}
		setBusy(id);
		setLoi("");
		try {
			const { data } = await http.patch(`/admin/partners/${props.partnerId}/payout-designations/${encodeURIComponent(id)}`, {
				status,
				note: status === "rejected" ? lyDo[id].trim() : undefined,
			});
			setDs(data?.data || []);
			props.onChanged?.();
		} catch (e: any) {
			// 30/10/2026: giấy do chính mình tải thay trạm ⇒ muốn tự duyệt phải ghi lý do (lưu vết).
			const boQua =
				e?.response?.data?.code === "SAME_PERSON" ? window.prompt(`${e.response.data.message}\n\nReason for approving it yourself:`) || "" : "";
			if (boQua.trim()) {
				try {
					const { data } = await http.patch(`/admin/partners/${props.partnerId}/payout-designations/${encodeURIComponent(id)}`, {
						status,
						overrideReason: boQua.trim(),
					});
					setDs(data?.data || []);
					props.onChanged?.();
					return;
				} catch (e2: any) {
					setLoi(e2?.response?.data?.message || "Could not save. Please try again.");
					return;
				}
			}
			setLoi(e?.response?.data?.message || "Could not save. Please try again.");
		} finally {
			setBusy("");
		}
	};
	if (!ds.length) return null;
	return (
		<div
			data-testid="payout-designations-admin"
			style={{ marginTop: 12, fontSize: 12.5, color: "#344054", minWidth: 0, overflowWrap: "anywhere" }}>
			<strong>Payout account designations</strong>
			{[...ds].reverse().map((c) => (
				<div
					key={c.id}
					style={{ borderTop: "1px solid #EAECF0", padding: "6px 0", display: "grid", gap: 4 }}>
					<span>
						<strong>{c.holderName}</strong> ·{" "}
						{c.mode === "representative" ? "account of the legal representative (tick)" : "designation letter"} · signed by {c.signerName}
						{c.submittedByEmail ? ` (${c.submittedByEmail})` : ""} · {new Date(c.submittedAt).toLocaleString()} ·{" "}
						<span style={{ color: MAU[c.status] || "#667085", fontWeight: 600 }}>{c.status}</span>
					</span>
					<span style={{ color: "#667085" }}>
						“{c.statement}” ({c.statementVersion}) · IP {c.ip || "—"} · {c.userAgent || "—"}
					</span>
					{c.files?.length ? (
						<span style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
							{c.files.map((f, i) => (
								<PrivateFileAnchor
									key={f}
									path={f}
									style={{ color: "#0F5F9E", fontWeight: 600 }}>
									📄 Letter {i + 1}
								</PrivateFileAnchor>
							))}
						</span>
					) : null}
					{c.reviewNote ? <span>Reason: {c.reviewNote}</span> : null}
					{c.status === "pending" ? (
						<span style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
							<button
								type="button"
								data-testid="payee-approve"
								disabled={busy === c.id}
								onClick={() => duyet(c.id, "approved")}
								style={{ border: "none", background: "#067647", color: "#fff", borderRadius: 8, padding: "4px 10px", cursor: "pointer" }}>
								Approve
							</button>
							<input
								placeholder="Reason if rejecting"
								value={lyDo[c.id] || ""}
								onChange={(e) => setLyDo({ ...lyDo, [c.id]: e.target.value })}
								style={{ padding: "4px 8px", border: "1px solid #D0D5DD", borderRadius: 8, flex: "1 1 200px", minWidth: 0, maxWidth: 360 }}
							/>
							<button
								type="button"
								data-testid="payee-reject"
								disabled={busy === c.id}
								onClick={() => duyet(c.id, "rejected")}
								style={{ border: "none", background: "#B42318", color: "#fff", borderRadius: 8, padding: "4px 10px", cursor: "pointer" }}>
								Reject
							</button>
						</span>
					) : null}
				</div>
			))}
			{loi ? <div style={{ color: "#B42318" }}>{loi}</div> : null}
		</div>
	);
}
